import { Tooltip, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { Clover24Regular } from '@fluentui/react-icons';
import { FC, useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { appInsights } from '../../AppInsights';
import { useUserContext } from '../../Contexts/UserContext';
import { SettingsMenuContext } from '../../Contexts/SettingsMenuContext';
import { AdminSettingsRoute, ExploreGptRoute, SettingsRoute } from '../../Routes';
import { useFeature, useFeatures, useGptConfigurations } from '../../store/hooks';
import { FeatureHighlightPopup } from '../Common/FeatureHighlightPopup';
import AppMenuButton from './AppMenuButton';
import { getUserInfoDebug } from '../../Utils/personaUtils';
import SyButton from '../Common/SyButton';
import Skeleton from 'react-loading-skeleton';
import { useRootState } from '../../store/hooks';
import {
    UserInteractionEventProperties,
    EventName_UserInteraction,
    EventProperty_Value_ApplicationType,
    EventProperty_Value_WikiClick,
} from '../../store/models/customEvents';
import { WIKI_URLS } from '../../constants/wikiUrls';

import { Settings, HelpCircle, Settings2 } from 'lucide-react';

interface MenuBottomProps {
    onCloseMenuRequired?: () => void;
}

const useStyles = makeStyles({
    root: {
        display: 'grid',
        gridTemplateRows: 'auto',
        rowGap: tokens.spacingVerticalM,
    },
    settingsButton: {
        width: '100%',
        justifyContent: 'flex-start',
        ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            ...shorthands.borderRadius(tokens.borderRadiusMedium),
        },
    },
});

const MenuBottom: FC<MenuBottomProps> = (props) => {
    const { onCloseMenuRequired } = props;
    const classes = useStyles();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { gptConfigurations } = useGptConfigurations();
    const [showExploreGpt, setShowExploreGpt] = useState(false);
    const chatFeature = useFeature('chat');
    const isPersonalGptEnabled = chatFeature.personalGpt?.isEnabled ?? false;
    const { userProfile } = useUserContext();
    const { setIsInSettings, setPreviousRoute } = useContext(SettingsMenuContext);
    const location = useLocation();
    const { state } = useRootState();
    const appLoading = state.appLoading ?? false;
    const conversationsLoading = state.conversationsLoading ?? false;
    const loading = appLoading || conversationsLoading;

    const { features } = useFeatures();
    const isAdminEnabled = chatFeature.isAdmin;

    // show SyGPT Tools button if there are any GPTs other than SyGPT
    useEffect(() => {
        setShowExploreGpt(gptConfigurations.some((gpt) => !gpt.isDefault && !gpt.isPersonal) || isPersonalGptEnabled);
    }, [gptConfigurations, isPersonalGptEnabled]);

    useEffect(() => {
        console.log('isAdmin:', isAdminEnabled);
        if (showExploreGpt) {
            appInsights.trackTrace({
                message:
                    'gptConfigurations in MenuBottom; isPersonalGptEnabled: ' +
                    isPersonalGptEnabled +
                    '; ' +
                    getUserInfoDebug(userProfile),
                properties: gptConfigurations,
            });
            console.log('---');
        }
    }, [showExploreGpt, isAdminEnabled]);

    const handleSettingsClick = () => {
        // Store the current route before navigating to settings
        setPreviousRoute(location.pathname);
        // Set the settings context to true to show the settings menu
        setIsInSettings(true);
        navigate(SettingsRoute);
        // Close menu in mobile mode when navigating to settings
        if (onCloseMenuRequired) {
            onCloseMenuRequired();
        }
    };
    const handleAdminSettingsClick = () => {
        navigate(AdminSettingsRoute);
        // Close menu in mobile mode when navigating to settings
        if (onCloseMenuRequired) {
            onCloseMenuRequired();
        }
    };
    const onWikiClick = () => {
        appInsights.trackEvent({
            name: EventName_UserInteraction,
            properties: {
                applicationType: EventProperty_Value_ApplicationType,
                userId: userProfile?.username,
                interactionType: EventProperty_Value_WikiClick,
            } as UserInteractionEventProperties,
        });
        window.open(WIKI_URLS.HOME, '_blank');
    };
    if (loading) {
        return (
            <div className={classes.root} style={{ paddingBottom: '12px' }}>
                <div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Skeleton className='skeleton-btn' />
                        <Skeleton className='skeleton-btn' />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.root} style={{ paddingBottom: '12px' }}>
            {/* Settings button */}
            <div>
                <Tooltip content={'Open application settings'} relationship='label'>
                    <SyButton
                        iconOnly
                        className={classes.settingsButton}
                        appearance='tertiary'
                        icon={<Settings size={16} />}
                        onClick={handleSettingsClick}
                        style={{ marginLeft: '3px' }}
                    ></SyButton>
                </Tooltip>

                <Tooltip content={'Open Wiki for help'} relationship='label'>
                    <SyButton
                        iconOnly
                        className={classes.settingsButton}
                        appearance='tertiary'
                        icon={<HelpCircle size={16} />}
                        onClick={onWikiClick}
                        style={{ marginLeft: '14px' }}
                    ></SyButton>
                </Tooltip>
            </div>
        </div>
    );
};

export default MenuBottom;
